import { FC, useEffect, useState } from 'react';
import Loader from './Loader';
import DMObjectModal from './DMObjectModal';
import DMVirtualExperience from './DMVirtualExperience';
import { ReadablePalette } from '../common/Color';
import { DMNameRule } from '../hooks/useDMNameRules';
import { DMItem, isDMVirtualExperience, isDMVirtualExperiencePart, resolveDMExperiencePart } from '../services/DigitaltMuseum';

interface DMSingleObjectProps {
  /** The object (from the context) */
  object: DMItem,
  palette: ReadablePalette,
  /** Background color of the view, for the object page */
  backgroundColor?: string,
  /** Names per language from the DM view (`settings.dmNameRules`) */
  nameRules?: DMNameRule[],
  /** Background behind the images (`settings.display.backgroundColor`) */
  imageBackgroundColor?: string
}

type ExperiencePart = Awaited<ReturnType<typeof resolveDMExperiencePart>>;

/**
 * A single DM object shown as the content of the DM view: a virtual experience in an iframe, a part of a virtual
 * experience (e.g. vm-scrollytell) inside the experience it belongs to like on DM, or otherwise the object page.
 * A part whose experience is not found is shown as an object. DM articles are shown by DMArticle, not here
 * @returns {JSX.Element} Component template
 */
const DMSingleObject: FC<DMSingleObjectProps> = ({ object, palette, backgroundColor, nameRules, imageBackgroundColor }) => {
  // Undefined while the experience of a part is looked up, null if it was not found
  const [experiencePart, setExperiencePart] = useState<ExperiencePart | null | undefined>(undefined);
  const partUuid = isDMVirtualExperiencePart(object.subtype) ? object.uuid : undefined;

  useEffect(() => {
    if (!partUuid) { return; }
    let isActive = true;
    setExperiencePart(undefined);
    resolveDMExperiencePart(partUuid)
      .then(result => { isActive && setExperiencePart(result); })
      .catch(() => { isActive && setExperiencePart(null); });
    return () => { isActive = false; };
  }, [partUuid]);

  if (isDMVirtualExperience(object.subtype)) {
    return <DMVirtualExperience variant="page" uuid={object.uuid || ""} ownerCode={object.ownerCode} title={object.title} palette={palette}/>;
  }
  if (partUuid && experiencePart === undefined) {
    return <Loader variant="fill"/>;
  }
  if (partUuid && experiencePart) {
    return <DMVirtualExperience variant="page" uuid={experiencePart.uuid} contentId={experiencePart.contentId}
      ownerCode={experiencePart.ownerCode || object.ownerCode} title={object.title} palette={palette}/>;
  }
  return <DMObjectModal variant="page" uniqueId={object.uniqueId} backgroundColor={backgroundColor} preview={object} nameRules={nameRules} imageBackgroundColor={imageBackgroundColor}/>;
}

export default DMSingleObject;
